// Slide-over with one writer's profile and every one of their books.
// Opened from any username on the stats page; staff-only API behind it.

import React, { useEffect, useState } from "react";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';
import { formatDate } from '../../utils/dates';
import { COUNTRY_BY_CODE } from '../Profile/countries';
import { fmtNum } from './charts';

const countryName = (code) => COUNTRY_BY_CODE[code] || code || 'غير محدد';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export const BOOK_SORTS = [
    { key: 'views', label: 'الأكثر قراءة' },
    { key: 'rating', label: 'الأعلى تقييمًا' },
];

export function sortBooks(books, key) {
    const list = [...books];
    if (key === 'rating') {
        // unrated books sink to the bottom; ties broken by how many agree
        return list.sort((a, b) => (b.avg_rating ?? -1) - (a.avg_rating ?? -1) || b.ratings - a.ratings || b.views - a.views);
    }
    return list.sort((a, b) => b.views - a.views);
}

export function SortToggle({ value, onChange, label = 'الترتيب' }) {
    return (
        <div className='st-ranges st-sort' role='group' aria-label={label}>
            {BOOK_SORTS.map(s => (
                <button key={s.key} type='button' className={value === s.key ? 'is-active' : ''}
                        onClick={() => onChange(s.key)} aria-pressed={value === s.key}>{s.label}</button>
            ))}
        </div>
    );
}

/** Books table shared by the overview and the writer panel. `onAuthor` makes author names clickable. */
export function BooksTable({ books, onAuthor, showAuthor = true, showStatus = false }) {
    if (!books.length) return <p className='st-empty'>لا كتب</p>;
    return (
        <div className='st-table-wrap'>
            <table className='st-table'>
                <thead><tr>
                    <th>الكتاب</th>{showAuthor && <th>الكاتب</th>}{showStatus && <th>الحالة</th>}
                    <th>القراءات</th><th>التقييم</th><th>الفصول</th><th>نُشر</th><th>آخر تعديل</th>
                </tr></thead>
                <tbody>
                    {books.map(b => (
                        <tr key={b.id}>
                            <td>{b.is_published
                                ? <a href={`/read/${b.id}`} target='_blank' rel='noopener noreferrer'>{b.title}</a>
                                : b.title}</td>
                            {showAuthor && <td>
                                {onAuthor
                                    ? <button type='button' className='st-link' onClick={() => onAuthor(b.author_username)}
                                              title={b.author !== b.author_username ? `@${b.author_username}` : undefined}>{b.author}</button>
                                    : b.author}
                            </td>}
                            {showStatus && <td>{b.is_published ? 'منشور' : <span className='st-muted'>مسودة</span>}</td>}
                            <td>{fmtNum(b.views)}</td>
                            <td>{b.avg_rating != null ? `${b.avg_rating.toLocaleString('ar-EG')} (${fmtNum(b.ratings)})` : '—'}</td>
                            <td>{fmtNum(b.chapters)}</td>
                            <td>{b.published_at ? formatDate(b.published_at) : '—'}</td>
                            <td>{b.updated_at ? formatDate(b.updated_at) : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function WriterPanel({ username, authTokens, onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState('views');

    useEffect(() => {
        let cancelled = false;
        setData(null); setError(null);
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/stats/users/${encodeURIComponent(username)}/`, { headers: authHeaders(authTokens) });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const d = await r.json();
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [username, authTokens]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const books = data ? sortBooks(data.books, sort) : [];

    return (
        <div className='st-drawer-backdrop' onClick={onClose}>
            <aside className='st-drawer' role='dialog' aria-modal='true' aria-label={`الكاتب ${username}`} onClick={e => e.stopPropagation()}>
                <header className='st-drawer-head'>
                    <div>
                        <h2>{username}</h2>
                        {data && <p>{countryName(data.country)} · انضم {formatDate(data.joined)}{data.is_staff && ' · مشرف'}</p>}
                    </div>
                    <button type='button' className='st-close' onClick={onClose} aria-label='إغلاق'>×</button>
                </header>

                {error && <p className='st-empty'>تعذر تحميل بيانات الكاتب.</p>}
                {!data && !error && <p className='st-empty'>جارٍ التحميل…</p>}

                {data && (
                    <>
                    <dl className='st-kv'>
                        <div><dt>البريد</dt><dd dir='ltr'>{data.email || '—'}</dd></div>
                        <div><dt>الدولة</dt><dd>{countryName(data.country)}</dd></div>
                        <div><dt>تاريخ الميلاد</dt><dd>{data.date_of_birth ? formatDate(data.date_of_birth) : '—'}</dd></div>
                        <div><dt>الدخول</dt><dd>{data.provider === 'google' ? 'Google' : 'كلمة مرور'}{data.two_factor_enabled && ' · تحقق ثنائي'}</dd></div>
                        <div><dt>البريد مؤكد</dt><dd>{data.verified ? 'نعم' : <span className='st-warn'>لا</span>}</dd></div>
                        <div><dt>آخر دخول</dt><dd>{fmtDateTime(data.last_login)}</dd></div>
                        <div><dt>وافق على الإرشادات</dt><dd>{data.guidelines_accepted_at ? `${formatDate(data.guidelines_accepted_at)} (${data.guidelines_version})` : 'لم يوافق بعد'}</dd></div>
                        <div><dt>النشاط</dt><dd>{fmtNum(data.activity.ratings_given)} تقييمًا · {fmtNum(data.activity.bookmarks)} علامة · {fmtNum(data.activity.topics)} موضوعًا · {fmtNum(data.activity.comments)} تعليقًا</dd></div>
                    </dl>
                    {data.bio && <p className='st-bio'>{data.bio}</p>}

                    <section className='st-tiles st-tiles-compact'>
                        <div className='st-tile'><span className='st-tile-label'>كتب</span><span className='st-tile-value'>{fmtNum(data.totals.books)}</span><span className='st-tile-foot'><span className='st-tile-delta'>{fmtNum(data.totals.published)} منشورة</span></span></div>
                        <div className='st-tile'><span className='st-tile-label'>القراءات</span><span className='st-tile-value'>{fmtNum(data.totals.views)}</span></div>
                        <div className='st-tile'><span className='st-tile-label'>التقييمات</span><span className='st-tile-value'>{fmtNum(data.totals.ratings)}</span></div>
                    </section>

                    <header className='st-card-head'>
                        <div><h3>الكتب</h3><p>المسودات مدرجة أيضًا، بلا رابط</p></div>
                        <SortToggle value={sort} onChange={setSort} />
                    </header>
                    <BooksTable books={books} showAuthor={false} showStatus />
                    </>
                )}
            </aside>
        </div>
    );
}
